import './accordion.agentsync-container.scss';

import * as React from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import { NewConvergeButton, NewFormTextField } from '../../../../presentation';
import {
  useConvergeNavigation,
  useConvergeSnackBar,
} from '../../../../contexts';
import {
  useUpsertBrokerAgentSync,
  useUpsertBrokerageAgentSync,
} from '@converge/graphql';

import { ApolloError } from '@apollo/client';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { Box } from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { config } from '@converge/config';
import { styled } from '@mui/material/styles';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  '&:first-child': {},
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginLeft: 'auto',
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
  '& .MuiAccordionSummary-root': {
    margin: '12px 0px',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export default function AgentSyncAccordions({
  isBroker = false,
}: {
  isBroker?: boolean;
}) {
  // Initialize with panel IDs that should be open by default
  const [expandedPanels, setExpandedPanels] = React.useState<string[]>(['']); // 'panel1' will be open by default
  const navigate = useConvergeNavigation();
  const { notify } = useConvergeSnackBar();

  const formMethods = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const { upsertEntity, loading } = useUpsertBrokerageAgentSync();
  const { upsertBroker, loading: brokerLoading } = useUpsertBrokerAgentSync();

  const handleUpsertEntity = async () => {
    try {
      if (isBroker) {
        const data2 = await upsertBroker({
          variables: { personNpn: formMethods.getValues('search') },
        });
        const entityUuid = data2 && data2?.data?.upsertPersonFromNpn?.uuid;
        if (entityUuid) {
          navigate.broker({
            brokerUuid: entityUuid,
          });
        }
      } else {
        const data2 = await upsertEntity({
          variables: { entityNpn: formMethods.getValues('search') },
        });

        const entityUuid = data2 && data2?.data?.upsertEntityFromNpn?.uuid;
        if (entityUuid) {
          navigate.brokerage({
            brokerageUuid: entityUuid,
          });
        }
      }
    } catch (cause) {
      if (cause instanceof ApolloError) {
        const errorMessage = cause?.graphQLErrors[0]?.message;
        if (errorMessage) {
          if (errorMessage.includes('Entity already exists in the system')) {
            notify('Brokerage already exists', { variant: 'error' });
            return;
          }
        }
        notify('Cannot find brokerage', { variant: 'error' });
      }
    }
  };

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanels((prev) =>
        isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
      );
    };

  const entityName = isBroker ? 'broker' : 'company';
  const entityField = 'NPN';

  const [panels] = React.useState([
    {
      id: 'panel1',
      title: `Find ${isBroker ? 'Broker' : 'Brokerage'} with AgentSync`,
      content: `Search for the ${entityName} using their ${entityField}. You can also enter and save the information in the sections below.`,
    },
  ]);

  return (
    <div className="agent-sync-container">
      <FormProvider {...formMethods}>
        {panels.map((panel) => (
          <Accordion
            key={panel.id}
            expanded={expandedPanels.includes(panel.id)}
            onChange={handleChange(panel.id)}
            className="accordion"
            sx={{ background: '#F2F2F2', borderTop: 'none' }}
          >
            <AccordionSummary
              aria-controls={`${panel.id}-content`}
              id={`${panel.id}-header`}
              sx={{ padding: '0px 24px' }}
              expandIcon={
                <GridExpandMoreIcon
                  className="expand-icon"
                  sx={{
                    transform:
                      expandedPanels[0] === 'panel1'
                        ? 'rotate(270deg)'
                        : 'rotate(-90deg)',
                  }}
                />
              }
            >
              <Typography className="title">{panel.title}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0px 8px' }}>
              {React.isValidElement(panel.content) ? (
                panel.content
              ) : (
                <div className="form-wrapper">
                  <Typography variant="body1">{panel.content}</Typography>
                  <Box className="search-wrapper">
                    <Box className="search">
                      <NewFormTextField
                        label={entityField}
                        name="search"
                        className="search-text"
                        autoComplete={false}
                        placeholder={`Enter ${entityField}`}
                      />
                    </Box>
                    <Box className="search-button">
                      <NewConvergeButton
                        loading={loading || brokerLoading}
                        sx={{ margin: '20px !important' }}
                        data-testid={`${
                          isBroker ? 'find broker' : 'find brokerage'
                        }`}
                        label={`FIND ${isBroker ? 'broker' : 'brokerage'}`}
                        onClick={handleUpsertEntity}
                      />{' '}
                    </Box>

                    <Box className="logo-wrapper">
                      <img
                        src={config.agentSync}
                        className="cloudIcon"
                        alt="upload"
                      />
                      +
                      <img
                        src={config.dnb}
                        className="cloudIcon"
                        alt="upload"
                      />
                    </Box>
                  </Box>
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </FormProvider>
    </div>
  );
}
